import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Mail, ListTodo, Bell, Webhook, History, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AutomationDialog, AutomationRow } from "./AutomationDialog";
import { ACTION_LABELS, ActionType } from "./AutomationConfigForm";
import { formatDistanceToNow } from "date-fns";

interface Stage { id: string; stage_name: string; color: string; stage_order: number }
interface LogRow { id: string; status: string; triggered_at: string; action_type: string; error_message: string | null }

interface Props {
  workflowId: string;
  stages: Stage[];
  orgId: string;
}

const ACTION_ICONS: Record<ActionType, any> = {
  send_email: Mail,
  create_task: ListTodo,
  send_notification: Bell,
  webhook: Webhook,
};

export function AutomationsManager({ workflowId, stages, orgId }: Props) {
  const [automations, setAutomations] = useState<AutomationRow[]>([]);
  const [logs, setLogs] = useState<LogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AutomationRow | null>(null);

  const sortedStages = useMemo(() => [...stages].sort((a, b) => a.stage_order - b.stage_order), [stages]);

  const load = async () => {
    setLoading(true);
    const [{ data: autoData }, { data: logData }] = await Promise.all([
      supabase.from("workflow_automations").select("*").eq("workflow_id", workflowId).order("created_at"),
      supabase
        .from("workflow_action_logs")
        .select("id, status, triggered_at, action_type, error_message")
        .eq("org_id", orgId)
        .order("triggered_at", { ascending: false })
        .limit(10),
    ]);
    setAutomations((autoData as any) || []);
    setLogs((logData as any) || []);
    setLoading(false);
  };

  useEffect(() => {
    if (workflowId && orgId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId, orgId]);

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this automation?")) return;
    const { error } = await supabase.from("workflow_automations").delete().eq("id", id);
    if (error) { toast.error("Failed to delete"); return; }
    toast.success("Deleted");
    load();
  };

  const summarize = (a: AutomationRow): string => {
    const c = a.action_config || {};
    if (a.action_type === "send_email") return `Email "${c.subject || "(no subject)"}" → ${c.to_field || "customer"}`;
    if (a.action_type === "create_task") return `Task "${c.title || "(untitled)"}" → ${c.assignee_field || "sales_rep"}`;
    if (a.action_type === "send_notification") return `Notify ${c.recipient_field || "sales_rep"}`;
    if (a.action_type === "webhook") return `${c.method || "POST"} ${c.url || "(no url)"}`;
    return "";
  };

  if (loading) {
    return (
      <Card><CardContent className="py-12 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </CardContent></Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold">Stage automations</h3>
          <p className="text-sm text-muted-foreground">Fire actions automatically when a job enters a stage</p>
        </div>
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" /> New automation
        </Button>
      </div>

      {sortedStages.map((stage) => {
        const rows = automations.filter((a) => a.stage_id === stage.id);
        return (
          <Card key={stage.id}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
                {stage.stage_name}
                <Badge variant="outline" className="ml-2">{rows.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {rows.length === 0 ? (
                <p className="text-sm text-muted-foreground">No automations on this stage.</p>
              ) : (
                <div className="space-y-2">
                  {rows.map((a) => {
                    const Icon = ACTION_ICONS[a.action_type];
                    return (
                      <div key={a.id} className="flex items-center gap-3 rounded-lg border p-3">
                        <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{ACTION_LABELS[a.action_type]}</p>
                          <p className="text-xs text-muted-foreground truncate">{summarize(a)}</p>
                        </div>
                        {!a.is_active && <Badge variant="secondary">Paused</Badge>}
                        <Button variant="ghost" size="icon" onClick={() => { setEditing(a); setDialogOpen(true); }}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(a.id!)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <History className="w-4 h-4" /> Recent fires
          </CardTitle>
          <CardDescription>Last 10 automation executions across this org</CardDescription>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No automations have fired yet.</p>
          ) : (
            <div className="space-y-1.5">
              {logs.map((l) => (
                <div key={l.id} className="flex items-center justify-between text-sm border-b py-1.5 last:border-0">
                  <div className="flex items-center gap-2">
                    <Badge variant={l.status === "completed" || l.status === "success" ? "default" : l.status === "failed" ? "destructive" : "secondary"}>
                      {l.status}
                    </Badge>
                    <span>{ACTION_LABELS[l.action_type as ActionType] || l.action_type}</span>
                    {l.error_message && <span className="text-xs text-destructive truncate max-w-xs">{l.error_message}</span>}
                  </div>
                  <span className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(l.triggered_at), { addSuffix: true })}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AutomationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        workflowId={workflowId}
        stages={sortedStages.map((s) => ({ id: s.id, stage_name: s.stage_name }))}
        initial={editing}
        onSaved={load}
      />
    </div>
  );
}
